import { Fragment, useRef, useState, type DragEvent, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, type Theme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RoleSummarySchema,
  WorkflowDefinitionSchema,
  WorkflowSubjectTypeSchema,
  type ApproverRuleDto,
  type WorkflowDefinitionDto,
  type WorkflowSubjectType,
} from '@taqon/contracts';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { z } from 'zod';
import { getData, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import {
  AllowApproversDialog,
  ApproverPermissionHint,
  canGrant,
  gapsFor,
  refusalMessage,
  rolesNamed,
  type RoleGap,
} from './approver-permissions';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import {
  ConditionEditor,
  DEFINITIONS,
  KEY,
  RuleEditor,
  conditionOf,
  describeCondition,
  describeRule,
  emptyStep,
  isGroup,
  membersOf,
  ruleComplete,
  termComplete,
  type StepDraft,
} from './WorkflowRoutesPage';

/**
 * The approval chart (SDD §14; FRD-CORE-011, CORE-013): every action that goes through approval,
 * and the flow of the selected one drawn as blocks and arrows. An administrator edits the flow by
 * dragging approver blocks into it and between its steps; blocks dropped together run in parallel.
 * The flow is saved as a draft version of the action's route and put in force deliberately, exactly
 * as on the Approval routes page: the version in force is never edited, so every decision stays
 * traceable to the route that governed it, and the API checks every route it is given.
 */

export interface Block extends StepDraft {
  readonly key: string;
}

export interface Stage {
  readonly key: string;
  readonly completion: 'all' | 'any';
  readonly blocks: readonly Block[];
}

/** Where a block is dropped: into the gap after a stage (null: before the first), or onto a stage. */
export type DropTarget =
  | { readonly kind: 'gap'; readonly after: string | null }
  | { readonly kind: 'stage'; readonly stage: string };

let counter = 0;
const nextKey = (prefix: string) => `${prefix}${String(++counter)}`;

/** A route version as stages: the steps sharing a sequence run in parallel. */
export function stagesOf(definition: WorkflowDefinitionDto | null): Stage[] {
  if (definition === null) return [];
  const bySequence = new Map<number, Block[]>();
  for (const step of [...definition.steps].sort((a, b) => a.sequence - b.sequence)) {
    const condition = membersOf(step.condition);
    const block: Block = {
      key: nextKey('b'),
      sequence: String(step.sequence),
      name: step.name,
      groupCompletion: step.groupCompletion,
      rule: step.approverRule,
      mode: condition.mode,
      members: condition.members,
      escalationHours: step.escalationAfterHours === null ? '' : String(step.escalationAfterHours),
      escalationRule: step.escalationTargetRule,
      escalationReassigns: step.escalationReassigns,
    };
    bySequence.set(step.sequence, [...(bySequence.get(step.sequence) ?? []), block]);
  }
  return [...bySequence.values()].map((blocks) => ({
    key: nextKey('s'),
    completion: blocks[0]?.groupCompletion ?? 'all',
    blocks,
  }));
}

/** Puts a block (new, or moved from where it was) at a drop target; empty stages close up. */
export function place(stages: readonly Stage[], block: Block, target: DropTarget): Stage[] {
  const without = stages.map((s) => ({
    ...s,
    blocks: s.blocks.filter((b) => b.key !== block.key),
  }));
  let next: Stage[];
  if (target.kind === 'stage') {
    next = without.map((s) =>
      s.key === target.stage ? { ...s, blocks: [...s.blocks, block] } : s,
    );
  } else {
    const index = target.after === null ? 0 : without.findIndex((s) => s.key === target.after) + 1;
    next = [...without];
    next.splice(index, 0, { key: nextKey('s'), completion: 'all', blocks: [block] });
  }
  return next.filter((s) => s.blocks.length > 0);
}

/**
 * The keyboard way to move a block (the same moves as dragging): earlier or later by one step. A
 * block alone in its step swaps places with the neighbouring step; a block running in parallel
 * with others becomes a step of its own just before or after them.
 */
export function shift(stages: readonly Stage[], key: string, direction: -1 | 1): Stage[] {
  const index = stages.findIndex((s) => s.blocks.some((b) => b.key === key));
  const stage = stages[index];
  const block = stage?.blocks.find((b) => b.key === key);
  if (stage === undefined || block === undefined) return [...stages];
  if (stage.blocks.length > 1) {
    const after = direction === -1 ? (stages[index - 1]?.key ?? null) : stage.key;
    return place(stages, block, { kind: 'gap', after });
  }
  const other = index + direction;
  if (other < 0 || other >= stages.length) return [...stages];
  const next = [...stages];
  [next[index], next[other]] = [next[other] as Stage, stage];
  return next;
}

/** The request body for a new version of the route. */
export function stepsOf(stages: readonly Stage[], type: WorkflowSubjectType) {
  return stages.flatMap((stage, index) =>
    stage.blocks.map((b) => ({
      sequence: index + 1,
      name: b.name.trim(),
      groupCompletion: stage.completion,
      approverRule: b.rule,
      ...(b.members.length > 0 ? { condition: conditionOf(b, type) } : {}),
      ...(b.escalationHours !== '' && b.escalationRule !== null
        ? {
            escalationAfterHours: Number(b.escalationHours),
            escalationTargetRule: b.escalationRule,
            escalationReassigns: b.escalationReassigns,
          }
        : {}),
    })),
  );
}

export function blockComplete(b: StepDraft): boolean {
  return (
    b.name.trim() !== '' &&
    ruleComplete(b.rule) &&
    b.members.every((m) =>
      isGroup(m) ? m.terms.length > 0 && m.terms.every(termComplete) : termComplete(m),
    ) &&
    (b.escalationHours === '' || (b.escalationRule !== null && ruleComplete(b.escalationRule)))
  );
}

/** The approver blocks an administrator drags into a flow: the closed set of rules (SDD §14.2). */
const PALETTE: readonly { kind: ApproverRuleDto['type']; label: string; detail: string }[] = [
  { kind: 'reporting_manager', label: 'Reporting manager', detail: "The employee's manager" },
  { kind: 'role', label: 'Role', detail: 'Holders of a role' },
  {
    kind: 'role_in_record_scope',
    label: 'Role for the record',
    detail: "A role for the record's department, cost centre, project or location",
  },
];

function newBlock(kind: ApproverRuleDto['type'], type: WorkflowSubjectType): Block {
  const base = emptyStep(1);
  if (kind === 'reporting_manager') {
    return { ...base, key: nextKey('b'), name: 'Line manager', rule: { type: kind } };
  }
  if (kind === 'role') return { ...base, key: nextKey('b'), rule: { type: kind, roleCode: '' } };
  const dimension = (['department', 'cost_center', 'project', 'location'] as const).find((d) =>
    type.routingAttributes.some((a) => a.name === DIMENSION_ATTRIBUTE[d]),
  );
  return {
    ...base,
    key: nextKey('b'),
    rule: { type: kind, roleCode: '', dimension: dimension ?? 'department' },
  };
}

const DIMENSION_ATTRIBUTE = {
  department: 'departmentId',
  cost_center: 'costCenterId',
  project: 'projectId',
  location: 'locationId',
} as const;

function tone(theme: Theme, rule: ApproverRuleDto['type']): string {
  switch (rule) {
    case 'role':
      return theme.palette.info.main;
    case 'role_in_record_scope':
      return theme.palette.warning.main;
    default:
      return theme.palette.success.main;
  }
}

const DRAG_TYPE = 'application/x-taqon-approval-block';
type Dragged = { from: 'palette'; kind: ApproverRuleDto['type'] } | { from: 'block'; key: string };

export function ApprovalChartPage() {
  const [params, setParams] = useSearchParams();
  const session = useSession();
  const types = useQuery({
    queryKey: ['platform', 'workflow-subject-types'],
    queryFn: () =>
      getData('/api/v1/platform/workflow-subject-types', z.array(WorkflowSubjectTypeSchema)),
  });
  const definitions = useQuery({
    queryKey: [...KEY, 'all'],
    queryFn: () => getData(DEFINITIONS, z.array(WorkflowDefinitionSchema)),
  });
  // Role names for the blocks; a session without platform.role:view sees role codes.
  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: () => getData('/api/v1/platform/roles', z.array(RoleSummarySchema)),
    enabled: hasCapability(session.data, 'platform.role:view'),
    retry: false,
  });
  const flowRef = useRef<HTMLDivElement>(null);
  const selected = types.data?.find((t) => t.recordType === params.get('type')) ?? types.data?.[0];
  const all = definitions.data ?? [];
  const versionsOf = (recordType: string) =>
    all
      .filter((d) => d.subjectRecordType === recordType)
      .sort((a, b) => b.definitionVersion - a.definitionVersion);

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Approval chart"
        description="Every action that needs approval, and who approves it in which order. Choose an action to see its flow; drag blocks to change it."
        actions={
          <Button component={RouterLink} to="/admin/workflows">
            Approval routes
          </Button>
        }
      />
      {(types.isPending || definitions.isPending) && (
        <LoadingState label="Loading the approval actions" />
      )}
      {types.isError && <ErrorState message={types.error.message} />}
      {definitions.isError && <ErrorState message={definitions.error.message} />}
      {types.data !== undefined && definitions.data !== undefined && (
        <>
          <Section title="Actions that need approval">
            {types.data.length === 0 ? (
              <EmptyState title="No action goes through approval yet" />
            ) : (
              <Box
                role="list"
                aria-label="Approval actions"
                sx={{
                  p: 2,
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                }}
              >
                {types.data.map((type) => (
                  <ActionCard
                    key={type.recordType}
                    type={type}
                    versions={versionsOf(type.recordType)}
                    selected={type.recordType === selected?.recordType}
                    onSelect={() => {
                      setParams({ type: type.recordType });
                      // jsdom has no scrollIntoView.
                      const flow = flowRef.current;
                      if (flow !== null && 'scrollIntoView' in flow) {
                        flow.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Section>
          {selected !== undefined && (
            <Box ref={flowRef} sx={{ scrollMarginTop: 80 }}>
              <Flow
                key={selected.recordType}
                type={selected}
                versions={versionsOf(selected.recordType)}
                roleNames={new Map((roles.data ?? []).map((r) => [r.code, r.name] as const))}
                roles={roles.data ?? []}
                canCreate={hasCapability(session.data, 'platform.workflow_definition:create')}
                canChange={hasCapability(session.data, 'platform.workflow_definition:change')}
                versionParam={params.get('version')}
                onVersion={(id) => {
                  setParams({ type: selected.recordType, ...(id === null ? {} : { version: id }) });
                }}
              />
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}

function ActionCard({
  type,
  versions,
  selected,
  onSelect,
}: {
  type: WorkflowSubjectType;
  versions: readonly WorkflowDefinitionDto[];
  selected: boolean;
  onSelect: () => void;
}) {
  const active = versions.find((d) => d.status === 'active');
  const drafts = versions.filter((d) => d.status === 'draft').length;
  const stages = active === undefined ? 0 : new Set(active.steps.map((s) => s.sequence)).size;
  return (
    <Box role="listitem">
      <ButtonBase
        aria-pressed={selected}
        onClick={onSelect}
        sx={(theme) => ({
          width: '100%',
          height: '100%',
          display: 'block',
          textAlign: 'left',
          px: 1.5,
          py: 1.25,
          borderRadius: 2,
          border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
          bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
          '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}` },
        })}
      >
        <Typography variant="subtitle2" component="span" sx={{ display: 'block' }}>
          {type.displayName}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ mt: 0.75, flexWrap: 'wrap' }}>
          {active === undefined ? (
            <Chip size="small" color="warning" variant="outlined" label="No route in force" />
          ) : (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`In force: version ${String(active.definitionVersion)}`}
            />
          )}
          {drafts > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={drafts === 1 ? '1 draft' : `${String(drafts)} drafts`}
            />
          )}
        </Stack>
        {active !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {stages === 1 ? '1 approval step' : `${String(stages)} approval steps`}
          </Typography>
        )}
      </ButtonBase>
    </Box>
  );
}

function versionLabel(d: WorkflowDefinitionDto): string {
  const status = d.status === 'active' ? 'in force' : d.status;
  return `Version ${String(d.definitionVersion)}: ${d.name} (${status})`;
}

function Flow({
  type,
  versions,
  roleNames,
  roles,
  canCreate,
  canChange,
  versionParam,
  onVersion,
}: {
  type: WorkflowSubjectType;
  versions: readonly WorkflowDefinitionDto[];
  roleNames: ReadonlyMap<string, string>;
  roles: readonly { code: string; name: string; status: string; isPrivileged?: boolean }[];
  canCreate: boolean;
  canChange: boolean;
  versionParam: string | null;
  onVersion: (id: string | null) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const active = versions.find((d) => d.status === 'active');
  const shown =
    versions.find((d) => d.id === versionParam) ??
    active ??
    versions.find((d) => d.status === 'draft') ??
    versions[0] ??
    null;
  const [editing, setEditing] = useState<{ name: string; stages: Stage[] } | null>(null);
  const [details, setDetails] = useState<{ block: Block; isNew: boolean } | null>(null);
  const [activating, setActivating] = useState<WorkflowDefinitionDto | null>(null);
  const [allowing, setAllowing] = useState<RoleGap[] | null>(null);
  const [checking, setChecking] = useState(false);
  // A step whose role cannot decide it is refused by the API; when this user may add permissions,
  // offer to allow the roles first instead of only reporting the refusal.
  const saveDraft = async (draft: { name: string; stages: Stage[] }) => {
    if (canGrant(session.data)) {
      setChecking(true);
      try {
        const steps = stepsOf(draft.stages, type);
        const gaps = await gapsFor(
          queryClient,
          type,
          rolesNamed(steps.flatMap((step) => [step.approverRule, step.escalationTargetRule])),
          roles,
        );
        if (gaps.length > 0) {
          setAllowing(gaps);
          return;
        }
      } catch {
        // The permissions could not be read: the API still checks the route on save.
      } finally {
        setChecking(false);
      }
    }
    save.mutate(draft);
  };

  const save = useMutation({
    mutationFn: (draft: { name: string; stages: Stage[] }) =>
      postJson(
        DEFINITIONS,
        {
          subjectRecordType: type.recordType,
          name: draft.name.trim(),
          steps: stepsOf(draft.stages, type),
        },
        WorkflowDefinitionSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async (saved) => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: KEY });
      onVersion(saved.id);
    },
  });
  const activate = useMutation({
    mutationFn: (def: WorkflowDefinitionDto) =>
      postJson(
        `${DEFINITIONS}/${def.id}/activate`,
        {},
        WorkflowDefinitionSchema,
        session.data?.csrfToken ?? '',
        {
          expectedVersion: def.version,
        },
      ),
    onSuccess: async () => {
      setActivating(null);
      await queryClient.invalidateQueries({ queryKey: KEY });
    },
  });

  const stages = editing?.stages ?? stagesOf(shown);
  const setStages = (next: Stage[]) => {
    if (editing !== null) setEditing({ ...editing, stages: next });
  };
  const blockCount = stages.reduce((n, s) => n + s.blocks.length, 0);
  const valid =
    editing !== null &&
    editing.name.trim() !== '' &&
    blockCount > 0 &&
    blockCount <= 30 &&
    stages.every((s) => s.blocks.every(blockComplete));

  const startEditing = () => {
    save.reset();
    setEditing({ name: shown?.name ?? `${type.displayName} approval`, stages: stagesOf(shown) });
  };
  const drop = (dragged: Dragged, target: DropTarget) => {
    if (editing === null) return;
    if (dragged.from === 'palette') {
      const block = newBlock(dragged.kind, type);
      setStages(place(stages, block, target));
      setDetails({ block, isNew: true });
      return;
    }
    const block = stages.flatMap((s) => s.blocks).find((b) => b.key === dragged.key);
    if (block !== undefined) setStages(place(stages, block, target));
  };

  return (
    <Section
      title={`${type.displayName}: approval flow`}
      actions={
        editing === null ? (
          <Stack direction="row" spacing={1}>
            {canChange && shown?.status === 'draft' && (
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  activate.reset();
                  setActivating(shown);
                }}
              >
                Put in force
              </Button>
            )}
            {canCreate && (
              <Button
                size="small"
                variant={shown?.status === 'draft' ? 'outlined' : 'contained'}
                startIcon={<EditOutlinedIcon />}
                onClick={startEditing}
              >
                {shown === null ? 'Create flow' : 'Edit flow'}
              </Button>
            )}
          </Stack>
        ) : undefined
      }
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        {editing === null && versions.length > 0 && shown !== null && (
          <TextField
            select
            size="small"
            label="Version"
            value={shown.id}
            sx={{ maxWidth: 480 }}
            onChange={(e) => {
              onVersion(e.target.value);
            }}
          >
            {versions.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {versionLabel(d)}
              </MenuItem>
            ))}
          </TextField>
        )}
        {editing === null && active === undefined && (
          <Alert severity="warning" variant="outlined">
            No route is in force: {type.displayName.toLowerCase()} records cannot be submitted for
            approval until one is put in force.
          </Alert>
        )}
        {editing === null && shown !== null && shown.status === 'draft' && (
          <Alert severity="info" variant="outlined">
            This is a draft. It is not used until it is put in force
            {active !== undefined ? `, replacing version ${String(active.definitionVersion)}` : ''}.
          </Alert>
        )}
        {editing === null &&
          shown !== null &&
          (shown.status === 'superseded' || shown.status === 'inactive') && (
            <Alert severity="info" variant="outlined">
              This version is no longer used. Approvals it started keep it until they finish.
            </Alert>
          )}
        {editing !== null && (
          <>
            <Alert severity="info" variant="outlined">
              Drag a block from below into a gap between steps to add a step, or onto a step to have
              it approve at the same time. Drag blocks to move them. Select a block to choose its
              approver and when it applies. Saving makes a draft; the version in force does not
              change until you put the draft in force.
            </Alert>
            {save.isError && (
              <Alert severity="error" variant="outlined">
                {refusalMessage(save.error, type, roles)}
              </Alert>
            )}
            <TextField
              label="Route name"
              required
              size="small"
              value={editing.name}
              sx={{ maxWidth: 480 }}
              onChange={(e) => {
                setEditing({ ...editing, name: e.target.value });
              }}
            />
            <Palette
              onAdd={(kind) => {
                drop({ from: 'palette', kind }, { kind: 'gap', after: stages.at(-1)?.key ?? null });
              }}
            />
          </>
        )}
        <Canvas
          stages={stages}
          type={type}
          roleNames={roleNames}
          editable={editing !== null}
          onDrop={drop}
          onOpen={(block) => {
            setDetails({ block, isNew: false });
          }}
          onShift={(key, direction) => {
            setStages(shift(stages, key, direction));
          }}
          onJoinPrevious={(key, previous) => {
            const block = stages.flatMap((s) => s.blocks).find((b) => b.key === key);
            if (block !== undefined)
              setStages(place(stages, block, { kind: 'stage', stage: previous }));
          }}
          onRemove={(key) => {
            setStages(
              stages
                .map((s) => ({ ...s, blocks: s.blocks.filter((b) => b.key !== key) }))
                .filter((s) => s.blocks.length > 0),
            );
          }}
          onCompletion={(stageKey, completion) => {
            setStages(stages.map((s) => (s.key === stageKey ? { ...s, completion } : s)));
          }}
        />
        {editing === null && (
          <Typography variant="caption" color="text.secondary">
            At any step an approver may approve, reject or return the record; a returned record can
            be changed and submitted again. A step with a condition is skipped when the record does
            not meet it.
          </Typography>
        )}
        {editing !== null && (
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button
              color="inherit"
              disabled={save.isPending}
              onClick={() => {
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!valid || save.isPending || checking}
              onClick={() => {
                void saveDraft(editing);
              }}
            >
              {save.isPending || checking ? 'Saving…' : 'Save as draft'}
            </Button>
          </Stack>
        )}
      </Stack>
      {details !== null && (
        <BlockDialog
          block={details.block}
          isNew={details.isNew}
          type={type}
          roles={roles}
          onClose={() => {
            setDetails(null);
          }}
          onSave={(block) => {
            setStages(
              stages.map((s) => ({
                ...s,
                blocks: s.blocks.map((b) => (b.key === block.key ? block : b)),
              })),
            );
            setDetails(null);
          }}
          onRemove={() => {
            setStages(
              stages
                .map((s) => ({ ...s, blocks: s.blocks.filter((b) => b.key !== details.block.key) }))
                .filter((s) => s.blocks.length > 0),
            );
            setDetails(null);
          }}
        />
      )}
      {allowing !== null && editing !== null && (
        <AllowApproversDialog
          gaps={allowing}
          type={type}
          onCancel={() => {
            setAllowing(null);
          }}
          onAllowed={() => {
            setAllowing(null);
            save.mutate(editing);
          }}
        />
      )}
      <ConfirmDialog
        open={activating !== null}
        title="Put route in force"
        message={`Version ${String(activating?.definitionVersion ?? '')} governs every new ${type.displayName.toLowerCase()} submission from now on${active !== undefined ? `, replacing version ${String(active.definitionVersion)}` : ''}. Approvals already running keep their route.`}
        confirmLabel="Put in force"
        tone="primary"
        busy={activate.isPending}
        error={activate.isError ? activate.error.message : null}
        onCancel={() => {
          setActivating(null);
        }}
        onConfirm={() => {
          if (activating !== null) activate.mutate(activating);
        }}
      />
    </Section>
  );
}

function readDragged(event: DragEvent): Dragged | null {
  try {
    const raw = event.dataTransfer.getData(DRAG_TYPE) || event.dataTransfer.getData('text/plain');
    const parsed = JSON.parse(raw) as Partial<Dragged> & { taqon?: boolean };
    if (parsed.taqon !== true) return null;
    if (parsed.from === 'palette' && typeof parsed.kind === 'string') {
      return { from: 'palette', kind: parsed.kind };
    }
    if (parsed.from === 'block' && typeof parsed.key === 'string') {
      return { from: 'block', key: parsed.key };
    }
  } catch {
    // Not one of ours: a file or text dragged in from elsewhere.
  }
  return null;
}

function startDrag(event: DragEvent, dragged: Dragged) {
  const data = JSON.stringify({ ...dragged, taqon: true });
  event.dataTransfer.setData(DRAG_TYPE, data);
  event.dataTransfer.setData('text/plain', data);
  event.dataTransfer.effectAllowed = 'move';
}

function Palette({ onAdd }: { onAdd: (kind: ApproverRuleDto['type']) => void }) {
  return (
    <Box>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        Approver blocks
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        role="list"
        aria-label="Approver blocks"
      >
        {PALETTE.map((item) => (
          <Paper
            key={item.kind}
            role="listitem"
            variant="outlined"
            draggable
            onDragStart={(e) => {
              startDrag(e, { from: 'palette', kind: item.kind });
            }}
            sx={(theme) => ({
              p: 1.25,
              pr: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'grab',
              borderLeft: `4px solid ${tone(theme, item.kind)}`,
              flex: 1,
            })}
          >
            <DragIndicatorIcon fontSize="small" color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.detail}
              </Typography>
            </Box>
            <Button
              size="small"
              aria-label={`Add a ${item.label.toLowerCase()} step at the end`}
              onClick={(event) => {
                event.currentTarget.focus();
                onAdd(item.kind);
              }}
            >
              Add
            </Button>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

interface CanvasProps {
  stages: readonly Stage[];
  type: WorkflowSubjectType;
  roleNames: ReadonlyMap<string, string>;
  editable: boolean;
  onDrop: (dragged: Dragged, target: DropTarget) => void;
  onOpen: (block: Block) => void;
  onShift: (key: string, direction: -1 | 1) => void;
  onJoinPrevious: (key: string, previousStage: string) => void;
  onRemove: (key: string) => void;
  onCompletion: (stage: string, completion: 'all' | 'any') => void;
}

/**
 * The flow, left to right on a wide screen and top to bottom on a phone: Submitted, each step,
 * Approved, joined by arrows. While editing, every arrow is also a place to drop a new step.
 */
function Canvas(props: CanvasProps) {
  const { stages, editable } = props;
  return (
    <Box
      role="group"
      aria-label="Approval flow"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        overflowX: 'auto',
        py: 2,
        px: { xs: 1, md: 0.5 },
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.025),
        backgroundImage: (theme) =>
          `radial-gradient(${alpha(theme.palette.text.primary, 0.08)} 1px, transparent 1px)`,
        backgroundSize: '16px 16px',
      }}
    >
      <Terminal label="Submitted" />
      <Gap after={null} {...props} />
      {stages.length === 0 && (
        <>
          <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center', minWidth: 200 }}>
            <Typography variant="body2">No approval steps</Typography>
            {editable && <Typography variant="caption">Drag a block here, or use Add</Typography>}
          </Box>
          <Arrow />
        </>
      )}
      {stages.map((stage, index) => (
        <Fragment key={stage.key}>
          <StageColumn
            stage={stage}
            index={index}
            previous={stages[index - 1]?.key ?? null}
            last={index === stages.length - 1}
            {...props}
          />
          <Gap after={stage.key} {...props} />
        </Fragment>
      ))}
      <Terminal label="Approved" end />
    </Box>
  );
}

function Terminal({ label, end = false }: { label: string; end?: boolean }) {
  return (
    <Box
      sx={(theme) => ({
        alignSelf: { xs: 'center', md: 'center' },
        flexShrink: 0,
        px: 1.25,
        py: 0.75,
        borderRadius: 999,
        fontWeight: 700,
        fontSize: 14,
        color: end ? theme.palette.success.contrastText : theme.palette.common.white,
        bgcolor: end ? theme.palette.success.main : '#1f2a2c',
      })}
    >
      {label}
    </Box>
  );
}

function Arrow({ active = false }: { active?: boolean }) {
  return (
    <ArrowForwardIcon
      aria-hidden
      sx={{
        color: active ? 'primary.main' : 'text.disabled',
        transform: { xs: 'rotate(90deg)', md: 'none' },
        alignSelf: 'center',
      }}
    />
  );
}

/** "Holders of Staging HR Access" → "holders of Staging HR Access": a role's name keeps its case. */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** The arrow between two steps; while editing, a drop here makes a new step at this point. */
function Gap({ after, editable, onDrop, stages }: CanvasProps & { after: string | null }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const position =
    after === null ? 'first' : `after step ${String(stages.findIndex((s) => s.key === after) + 1)}`;
  if (!editable) {
    return (
      <Box
        sx={{
          px: { xs: 1, md: 0.5 },
          py: 0.5,
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Arrow />
      </Box>
    );
  }
  return (
    <Box
      data-drop="gap"
      aria-label={`Drop here to add a step ${position}`}
      onDragEnter={(e) => {
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDragLeave={() => {
        depth.current -= 1;
        if (depth.current <= 0) setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const dragged = readDragged(e);
        if (dragged !== null) onDrop(dragged, { kind: 'gap', after });
      }}
      sx={(theme) => ({
        flexShrink: 0,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: { md: 28 },
        minHeight: { xs: 40, md: 'auto' },
        mx: { md: 0.25 },
        my: { xs: 0.5, md: 0 },
        borderRadius: 2,
        border: `2px dashed ${over ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.15)}`,
        bgcolor: over ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
        transition: theme.transitions.create(['background-color', 'border-color']),
      })}
    >
      <Arrow active={over} />
    </Box>
  );
}

function StageColumn({
  stage,
  index,
  previous,
  last,
  ...props
}: CanvasProps & { stage: Stage; index: number; previous: string | null; last: boolean }) {
  const { editable, onDrop, onCompletion } = props;
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const parallel = stage.blocks.length > 1;
  const handlers = editable
    ? {
        onDragEnter: (e: DragEvent) => {
          e.preventDefault();
          depth.current += 1;
          setOver(true);
        },
        onDragOver: (e: DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        },
        onDragLeave: () => {
          depth.current -= 1;
          if (depth.current <= 0) setOver(false);
        },
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          depth.current = 0;
          setOver(false);
          const dragged = readDragged(e);
          if (dragged !== null) onDrop(dragged, { kind: 'stage', stage: stage.key });
        },
      }
    : {};
  return (
    <Box
      role="group"
      aria-label={`Step ${String(index + 1)}`}
      data-drop={editable ? 'stage' : undefined}
      {...handlers}
      sx={(theme) => ({
        // Steps share the width, so four fit a 1280-pixel laptop screen while editing; more scroll.
        flex: { md: '1 1 0' },
        minWidth: { md: 140 },
        maxWidth: { md: 260 },
        p: 1,
        borderRadius: 2,
        border: `1px ${editable ? 'dashed' : 'solid'} ${over ? theme.palette.primary.main : parallel || editable ? alpha(theme.palette.text.primary, 0.18) : 'transparent'}`,
        bgcolor: over
          ? alpha(theme.palette.primary.main, 0.08)
          : parallel
            ? alpha(theme.palette.background.paper, 0.7)
            : 'transparent',
      })}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.75, minHeight: 28 }}>
        <Typography
          variant="overline"
          sx={{ lineHeight: 1.5, fontWeight: 700 }}
          color="text.secondary"
        >
          Step {index + 1}
        </Typography>
        {parallel &&
          (editable ? (
            <TextField
              select
              size="small"
              variant="standard"
              aria-label={`Step ${String(index + 1)} completes when`}
              value={stage.completion}
              onChange={(e) => {
                onCompletion(stage.key, e.target.value === 'any' ? 'any' : 'all');
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0 } }}
            >
              <MenuItem value="all">all approve</MenuItem>
              <MenuItem value="any">any one approves</MenuItem>
            </TextField>
          ) : (
            <Chip
              size="small"
              variant="outlined"
              label={
                stage.completion === 'all'
                  ? 'in parallel: all approve'
                  : 'in parallel: any one approves'
              }
            />
          ))}
      </Stack>
      <Stack spacing={1}>
        {stage.blocks.map((block) => (
          <BlockCard
            key={block.key}
            block={block}
            first={index === 0}
            last={last}
            previous={previous}
            parallel={parallel}
            {...props}
          />
        ))}
      </Stack>
      {editable && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
        >
          Drop here to approve at the same time
        </Typography>
      )}
    </Box>
  );
}

function BlockCard({
  block,
  first,
  last,
  previous,
  parallel,
  type,
  roleNames,
  editable,
  onOpen,
  onShift,
  onJoinPrevious,
  onRemove,
}: CanvasProps & {
  block: Block;
  first: boolean;
  last: boolean;
  previous: string | null;
  parallel: boolean;
}) {
  const complete = blockComplete(block);
  const condition =
    block.members.length > 0 ? describeCondition(conditionOf(block, type), type) : null;
  const name = block.name.trim() === '' ? 'New step' : block.name;
  return (
    <Paper
      draggable={editable}
      onDragStart={
        editable
          ? (e) => {
              startDrag(e, { from: 'block', key: block.key });
            }
          : undefined
      }
      elevation={editable ? 1 : 0}
      sx={(theme) => ({
        position: 'relative',
        borderRadius: 2,
        border: `1px solid ${complete ? theme.palette.divider : theme.palette.error.main}`,
        borderLeft: `5px solid ${tone(theme, block.rule.type)}`,
        cursor: editable ? 'grab' : 'default',
        overflow: 'hidden',
      })}
    >
      <ButtonBase
        disabled={!editable}
        aria-label={editable ? `Edit ${name}` : undefined}
        onClick={(event) => {
          event.currentTarget.focus();
          onOpen(block);
        }}
        sx={{ display: 'block', width: '100%', textAlign: 'left', p: 1.25 }}
      >
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          {editable && (
            <DragIndicatorIcon fontSize="small" color="action" sx={{ mt: 0.25, ml: -0.5 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ruleComplete(block.rule)
                ? describeRule(block.rule, roleNames)
                : 'Choose the role that approves'}
            </Typography>
            {condition !== null && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'warning.dark' }}
              >
                Only when {condition}
              </Typography>
            )}
            {block.escalationHours !== '' && block.escalationRule !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Escalates after {block.escalationHours} h to{' '}
                {lowerFirst(describeRule(block.escalationRule, roleNames))}
              </Typography>
            )}
            {!complete && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                Needs details: select to complete
              </Typography>
            )}
          </Box>
        </Stack>
      </ButtonBase>
      {editable && (
        <>
          <Divider />
          <Stack direction="row" sx={{ px: 0.5, justifyContent: 'space-between' }}>
            <Stack direction="row">
              <Tooltip title="Move earlier">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${name} earlier`}
                    disabled={first && !parallel}
                    onClick={() => {
                      onShift(block.key, -1);
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move later">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${name} later`}
                    disabled={last && !parallel}
                    onClick={() => {
                      onShift(block.key, 1);
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {previous !== null && (
                <Tooltip title="Approve at the same time as the previous step">
                  <IconButton
                    size="small"
                    aria-label={`Run ${name} with the previous step`}
                    onClick={() => {
                      onJoinPrevious(block.key, previous);
                    }}
                  >
                    <MergeTypeIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Tooltip title="Remove">
              <IconButton
                size="small"
                aria-label={`Remove ${name}`}
                onClick={() => {
                  onRemove(block.key);
                }}
              >
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      )}
    </Paper>
  );
}

/** One block's details: its name, approver, when it applies and its escalation. */
function BlockDialog({
  block,
  isNew,
  type,
  roles,
  onClose,
  onSave,
  onRemove,
}: {
  block: Block;
  isNew: boolean;
  type: WorkflowSubjectType;
  roles: readonly { code: string; name: string; status: string; isPrivileged?: boolean }[];
  onClose: () => void;
  onSave: (block: Block) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState<Block>(block);
  const update = (patch: Partial<StepDraft>) => {
    setDraft({ ...draft, ...patch });
  };
  const body: ReactNode = (
    <Stack spacing={2.5} sx={{ pt: 1 }}>
      <TextField
        label="Step name"
        required
        autoFocus
        value={draft.name}
        onChange={(e) => {
          update({ name: e.target.value });
        }}
        helperText="What approvers and requesters see, for example Finance or Department head."
      />
      <RuleEditor
        label="Approver"
        rule={draft.rule}
        roles={roles}
        type={type}
        onChange={(rule) => {
          update({ rule });
        }}
      />
      <ApproverPermissionHint rule={draft.rule} type={type} roles={roles} />
      <ConditionEditor
        type={type}
        mode={draft.mode}
        members={draft.members}
        onChange={(mode, members) => {
          update({ mode, members });
        }}
      />
      <Divider />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' } }}
      >
        <TextField
          label="Escalate after (hours)"
          value={draft.escalationHours}
          sx={{ width: { sm: 200 } }}
          onChange={(e) => {
            const hours = e.target.value.replace(/\D/g, '');
            update({
              escalationHours: hours,
              escalationRule:
                hours === '' ? null : (draft.escalationRule ?? { type: 'reporting_manager' }),
            });
          }}
          helperText="Leave empty for no escalation."
        />
        {draft.escalationHours !== '' && draft.escalationRule !== null && (
          <>
            <RuleEditor
              label="Escalate to"
              rule={draft.escalationRule}
              roles={roles}
              type={type}
              onChange={(rule) => {
                update({ escalationRule: rule });
              }}
            />
            <ApproverPermissionHint rule={draft.escalationRule} type={type} roles={roles} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.escalationReassigns}
                  onChange={(e) => {
                    update({ escalationReassigns: e.target.checked });
                  }}
                />
              }
              label="They may decide the step"
            />
          </>
        )}
      </Stack>
    </Stack>
  );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isNew ? 'New step' : `Step: ${block.name || 'new step'}`}</DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        <Button color="warning" startIcon={<DeleteOutlinedIcon />} onClick={onRemove}>
          Remove step
        </Button>
        <Stack direction="row" spacing={1}>
          <Button color="inherit" onClick={onClose}>
            {isNew ? 'Later' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            disabled={!blockComplete(draft)}
            onClick={() => {
              onSave(draft);
            }}
          >
            Done
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
